import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const projectTypes = [
  'New Home Construction',
  'Home Renovation',
  'Kitchen Remodel',
  'Bathroom Remodel',
  'Commercial Build',
  'Tenant Improvement',
  'Addition',
  'Outdoor Living',
  'Other',
];

const budgetRanges = [
  'Under $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  '$250,000 - $500,000',
  'Over $500,000',
];

const timelines = [
  'ASAP',
  '1-3 Months',
  '3-6 Months',
  '6-12 Months',
  'Flexible',
];

export const Contact = () => {
  const [searchParams] = useSearchParams();
  const showQuote = searchParams.get('quote') === 'true';
  
  const [activeTab, setActiveTab] = useState(showQuote ? 'quote' : 'contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  // Quote form state
  const [quoteForm, setQuoteForm] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    project_type: '',
    project_scope: '',
    budget_range: '',
    timeline: '',
    additional_notes: '',
  });

  useEffect(() => {
    if (showQuote) {
      setActiveTab('quote');
    }
  }, [showQuote]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...contactForm,
        subject: 'Contact Form Submission',
      });

      if (response.data.success) {
        setSubmitted(true);
        toast.success('Message sent successfully!', {
          description: response.data.message,
        });
        setContactForm({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!quoteForm.project_type) {
      toast.error('Please select a project type');
      return;
    }
    if (!quoteForm.project_scope || quoteForm.project_scope.length < 20) {
      toast.error('Please describe your project (at least 20 characters)');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote`, quoteForm);

      if (response.data.success) {
        setSubmitted(true);
        toast.success('Quote request submitted!', {
          description: response.data.message,
        });
        setQuoteForm({
          full_name: '',
          company_name: '',
          email: '',
          phone: '',
          project_type: '',
          project_scope: '',
          budget_range: '',
          timeline: '',
          additional_notes: '',
        });
      }
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error('Failed to submit quote request', {
        description: 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-48" data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="py-20 bg-ktm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <span className="inline-block bg-ktm-red text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-6">
              Get In Touch
            </span>
            <h1 className="font-chivo font-black text-4xl md:text-5xl text-ktm-navy leading-tight mb-6">
              Contact<br />
              <span className="text-ktm-red">KTM Construction</span>
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed">
              Ready to start your project? Have questions about our services? 
              We&apos;re here to help. Reach out to us and let&apos;s build something great together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info + Forms */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div 
              className="lg:col-span-1 space-y-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <h3 className="font-chivo font-bold text-xl text-ktm-navy mb-6">
                  Contact Information
                </h3>
                <ul className="space-y-6">
                  <li>
                    <a 
                      href="tel:916-872-1882"
                      className="flex items-start gap-4 group"
                      data-testid="contact-phone"
                    >
                      <div className="w-12 h-12 bg-ktm-navy flex items-center justify-center flex-shrink-0 group-hover:bg-ktm-red transition-colors">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-ktm-navy">Phone</div>
                        <div className="text-slate-600">916-872-1882</div>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="mailto:ktmconstruction21@gmail.com"
                      className="flex items-start gap-4 group"
                      data-testid="contact-email"
                    >
                      <div className="w-12 h-12 bg-ktm-navy flex items-center justify-center flex-shrink-0 group-hover:bg-ktm-red transition-colors">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-ktm-navy">Email</div>
                        <div className="text-slate-600">ktmconstruction21@gmail.com</div>
                      </div>
                    </a>
                  </li>
                  <li>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-ktm-navy flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-ktm-navy">Address</div>
                        <div className="text-slate-600">
                          8331 Sierra College Blvd<br />
                          Suite 208<br />
                          Roseville, CA
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-ktm-navy flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-ktm-navy">Business Hours</div>
                        <div className="text-slate-600">
                          Mon - Fri: 7:30 AM - 4:30 PM<br />
                          Sat: By Appointment<br />
                          Sun: Closed
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Map */}
              <div className="aspect-[4/3] bg-ktm-muted border border-ktm-border overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3116.2891434877455!2d-121.25961088465!3d38.75953677959!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b227f4d0a4b7f%3A0x7f7c7c7c7c7c7c7c!2s8331%20Sierra%20College%20Blvd%2C%20Roseville%2C%20CA%2095661!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="KTM Construction Location"
                />
              </div>
            </motion.div>

            {/* Forms */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Tab Switcher */}
              <div className="flex mb-8 border border-ktm-border" data-testid="form-tabs">
                <button
                  onClick={() => { setActiveTab('contact'); setSubmitted(false); }}
                  data-testid="tab-contact"
                  className={`flex-1 py-4 font-bold uppercase tracking-wider text-sm transition-colors ${
                    activeTab === 'contact'
                      ? 'bg-ktm-navy text-white'
                      : 'bg-white text-ktm-navy hover:bg-ktm-muted'
                  }`}
                >
                  Contact Us
                </button>
                <button
                  onClick={() => { setActiveTab('quote'); setSubmitted(false); }}
                  data-testid="tab-quote"
                  className={`flex-1 py-4 font-bold uppercase tracking-wider text-sm transition-colors ${
                    activeTab === 'quote'
                      ? 'bg-ktm-red text-white'
                      : 'bg-white text-ktm-navy hover:bg-ktm-muted'
                  }`}
                >
                  Free Quote
                </button>
              </div>

              {/* Success Message */}
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 p-8 text-center mb-8"
                  data-testid="success-message"
                >
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-chivo font-bold text-xl text-green-800 mb-2">
                    {activeTab === 'quote' ? 'Quote Request Submitted!' : 'Message Sent!'}
                  </h3>
                  <p className="text-green-700">
                    {activeTab === 'quote' 
                      ? "We'll review your project and contact you within 2-3 business days."
                      : "We'll get back to you within 24 hours."}
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 bg-green-600 text-white hover:bg-green-700"
                  >
                    Send Another
                  </Button>
                </motion.div>
              )}

              {/* Contact Form */}
              {activeTab === 'contact' && !submitted && (
                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-ktm-navy font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-name-input"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="John Doe"
                        required
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-navy"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-ktm-navy font-semibold">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-navy"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-ktm-navy font-semibold">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-navy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-ktm-navy font-semibold">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us about your project or ask a question..."
                      required
                      rows={6}
                      className="rounded-none border-2 border-slate-200 focus:border-ktm-navy resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full md:w-auto bg-ktm-navy text-white h-14 px-8 rounded-none font-bold uppercase tracking-wider btn-shadow hover:bg-ktm-navy/90 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}

              {/* Quote Form */}
              {activeTab === 'quote' && !submitted && (
                <form onSubmit={handleQuoteSubmit} className="space-y-6" data-testid="quote-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-ktm-navy font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="full_name"
                        data-testid="quote-name-input"
                        value={quoteForm.full_name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, full_name: e.target.value })}
                        placeholder="John Doe"
                        required
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-ktm-navy font-semibold">
                        Company Name (Optional)
                      </Label>
                      <Input
                        id="company_name"
                        data-testid="quote-company-input"
                        value={quoteForm.company_name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, company_name: e.target.value })}
                        placeholder="Your Company"
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quote_email" className="text-ktm-navy font-semibold">
                        Email Address *
                      </Label>
                      <Input
                        id="quote_email"
                        type="email"
                        data-testid="quote-email-input"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote_phone" className="text-ktm-navy font-semibold">
                        Phone Number *
                      </Label>
                      <Input
                        id="quote_phone"
                        type="tel"
                        data-testid="quote-phone-input"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        required
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="project_type" className="text-ktm-navy font-semibold">
                        Project Type *
                      </Label>
                      <Select
                        value={quoteForm.project_type}
                        onValueChange={(value) => setQuoteForm({ ...quoteForm, project_type: value })}
                        required
                      >
                        <SelectTrigger 
                          data-testid="quote-project-type-select"
                          className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                        >
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budget_range" className="text-ktm-navy font-semibold">
                        Budget Range
                      </Label>
                      <Select
                        value={quoteForm.budget_range}
                        onValueChange={(value) => setQuoteForm({ ...quoteForm, budget_range: value })}
                      >
                        <SelectTrigger 
                          data-testid="quote-budget-select"
                          className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                        >
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeline" className="text-ktm-navy font-semibold">
                      Desired Timeline
                    </Label>
                    <Select
                      value={quoteForm.timeline}
                      onValueChange={(value) => setQuoteForm({ ...quoteForm, timeline: value })}
                    >
                      <SelectTrigger 
                        data-testid="quote-timeline-select"
                        className="h-12 rounded-none border-2 border-slate-200 focus:border-ktm-red"
                      >
                        <SelectValue placeholder="When do you want to start?" />
                      </SelectTrigger>
                      <SelectContent>
                        {timelines.map((timeline) => (
                          <SelectItem key={timeline} value={timeline}>
                            {timeline}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project_scope" className="text-ktm-navy font-semibold">
                      Project Description *
                    </Label>
                    <Textarea
                      id="project_scope"
                      data-testid="quote-scope-input"
                      value={quoteForm.project_scope}
                      onChange={(e) => setQuoteForm({ ...quoteForm, project_scope: e.target.value })}
                      placeholder="Please describe your project in detail. Include any specific requirements, materials, or features you'd like..."
                      required
                      rows={6}
                      className="rounded-none border-2 border-slate-200 focus:border-ktm-red resize-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes" className="text-ktm-navy font-semibold">
                      Additional Notes
                    </Label>
                    <Textarea
                      id="additional_notes"
                      data-testid="quote-notes-input"
                      value={quoteForm.additional_notes}
                      onChange={(e) => setQuoteForm({ ...quoteForm, additional_notes: e.target.value })}
                      placeholder="Any other information you'd like to share..."
                      rows={3}
                      className="rounded-none border-2 border-slate-200 focus:border-ktm-red resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="quote-submit-btn"
                    className="w-full md:w-auto bg-ktm-red text-white h-14 px-8 rounded-none font-bold uppercase tracking-wider btn-shadow hover:bg-ktm-red/90 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
